import classNames from 'classnames';
import React, { Suspense, Children } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { ErrorMessage } from '@eika/attention-message';

import useTranslation, { format } from '../../../hooks/useTranslation';
import NotificationSettingsCategorySkeleton from './skeleton/NotificationSettingsCategorySkeleton';

type Props = {
    header?: string;
    children: React.ReactNode | React.ReactNode[];
    enabled?: boolean;
    // @default 'toggles'
    type?: 'settings' | 'toggles';
};

const NotificationCategory: React.FC<Props> = ({ header, children, type = 'toggles', enabled = true }) => {
    if (!enabled) return null;
    return (
        <section className="notifications-settings__category">
            {header && (
                <div className="notifications-settings__category-header-wrapper bold">
                    <h2 className="notifications-settings__category-header">{header}</h2>
                </div>
            )}
            {children && (
                <div
                    className={classNames('notifications-settings__content-wrapper', {
                        'notifications-settings__content-wrapper--settings': type === 'settings',
                    })}
                >
                    {children}
                </div>
            )}
        </section>
    );
};

type PropsWithStateLabel = {
    stateLabel?: string;
    customSkeleton?: React.ReactNode;
} & Props;

export default ({ children, header, stateLabel, customSkeleton, ...rest }: PropsWithStateLabel) => {
    const { pushNotifications: pushNotificationTexts } = useTranslation();
    return (
        <ErrorBoundary
            fallback={
                <NotificationCategory header={header}>
                    <ErrorMessage
                        text={format(pushNotificationTexts.couldNotGetNotificationsFor, stateLabel ?? header ?? '')}
                    />
                </NotificationCategory>
            }
        >
            <Suspense
                fallback={
                    customSkeleton ?? (
                        <NotificationSettingsCategorySkeleton
                            count={Children.count(children)}
                            header={header ?? stateLabel ?? ''}
                        />
                    )
                }
            >
                <NotificationCategory header={header} {...rest}>
                    {children}
                </NotificationCategory>
            </Suspense>
        </ErrorBoundary>
    );
};
